import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Translation resources for top 10 languages
const resources = {
  en: {
    translation: {
      // Common
      common: {
        loading: 'Loading...',
        save: 'Save',
        cancel: 'Cancel',
        delete: 'Delete',
        edit: 'Edit',
        submit: 'Submit',
        search: 'Search',
        back: 'Back',
        next: 'Next',
        previous: 'Previous',
        yes: 'Yes',
        no: 'No',
        confirm: 'Confirm',
        close: 'Close',
        error: 'Error',
        success: 'Success',
        warning: 'Warning',
        info: 'Information'
      },
      // Navigation
      nav: {
        home: 'Home',
        dashboard: 'Dashboard',
        profile: 'Profile',
        settings: 'Settings',
        logout: 'Logout',
        login: 'Login',
        register: 'Register'
      },
      // Auth
      auth: {
        email: 'Email',
        password: 'Password',
        confirmPassword: 'Confirm Password',
        forgotPassword: 'Forgot Password?',
        loginTitle: 'Sign In',
        registerTitle: 'Create Account',
        loginWithGoogle: 'Continue with Google',
        loginWithLinkedIn: 'Continue with LinkedIn',
        orContinueWith: 'Or continue with',
        noAccount: "Don't have an account?",
        haveAccount: 'Already have an account?'
      },
      // GED Dashboard
      ged: {
        title: 'GED Progress Dashboard',
        subjects: {
          math: 'Math',
          rla: 'Reading & Language Arts',
          science: 'Science',
          socialStudies: 'Social Studies'
        },
        readiness: {
          notReady: 'Not Ready',
          almostReady: 'Almost Ready',
          ready: 'Ready'
        },
        progress: 'Progress',
        studyStreak: 'Study Streak',
        daysActive: 'Days Active',
        subjectsRemaining: 'Subjects Remaining',
        nextAction: 'What should I do next?',
        continueLearning: 'Continue Learning',
        takePracticeTest: 'Take Practice Test',
        viewCoaches: 'View Coaches',
        bookSession: 'Book Session',
        joinSession: 'Join Session',
        library: 'GED Library',
        reports: 'Progress Reports',
        coaching: 'Coaching'
      },
      // Chat
      chat: {
        title: 'Messages',
        newMessage: 'New Message',
        typeMessage: 'Type a message...',
        send: 'Send',
        online: 'Online',
        offline: 'Offline',
        typing: 'typing...',
        noMessages: 'No messages yet',
        startConversation: 'Start a conversation'
      },
      // Notifications
      notifications: {
        title: 'Notifications',
        preferences: 'Notification Preferences',
        smsEnabled: 'SMS Notifications',
        pushEnabled: 'Push Notifications',
        emailEnabled: 'Email Notifications',
        sessionReminders: 'Session Reminders',
        studyNudges: 'Study Reminders',
        progressUpdates: 'Progress Updates',
        saved: 'Preferences saved',
        saveFailed: 'Failed to save preferences',
        quietHours: 'Quiet Hours',
        testNotification: 'Send Test Notification'
      },
      pricing: {
        title: 'Simple, Transparent Pricing',
        subtitle: 'Choose the plan that fits your goals',
        monthly: 'Monthly',
        yearly: 'Yearly',
        save: 'Save',
        mostPopular: 'Most Popular',
        startFree: 'Start Free',
        startTrial: 'Start Trial',
        subscribe: 'Subscribe',
        contactSales: 'Contact Sales',
        perMonth: '/month',
        perYear: '/year',
        faq: 'Frequently Asked Questions',
        securePayment: 'Secure Payment',
        moneyBack: '30-Day Guarantee',
        cancelAnytime: 'Cancel Anytime'
      },
      fileUpload: {
        upload: 'Upload',
        uploading: 'Uploading...',
        fileTooLarge: 'File too large. Maximum size is 10MB',
        fileTypeNotAllowed: 'File type not allowed',
        uploadFailed: 'Failed to upload file',
        sendFile: 'Send File',
        attachFile: 'Attach File',
        dragDrop: 'Drag and drop files here'
      },
      admin: {
        integrations: {
          title: 'Integration Management',
          subtitle: 'Manage credentials, test connections, and monitor health',
          status: 'Status',
          credentials: 'Credentials',
          health: 'Health',
          alerts: 'Alerts',
          configured: 'Configured',
          notConfigured: 'Not Configured',
          healthy: 'Healthy',
          unhealthy: 'Unhealthy',
          testConnection: 'Test Connection',
          saveCredentials: 'Save Credentials',
          runHealthCheck: 'Run Health Check',
          allHealthy: 'All integrations are healthy!',
          someFailed: 'Some integrations failed health check',
          lastChecked: 'Last checked',
          manageCredentials: 'Manage Credentials',
          selectIntegration: 'Select an integration above to manage its credentials',
          alertSettings: 'Alert Configuration',
          emailAlerts: 'Email Alerts',
          alertOnFailure: 'Alert on Failure',
          alertOnRecovery: 'Alert on Recovery',
          checkInterval: 'Health Check Interval'
        }
      },
      simulations: {
        title: 'Interactive Simulations',
        subtitle: 'Learn through hands-on virtual labs and visualizations',
        active: 'Active',
        comingSoon: 'Coming Soon',
        browseOthers: 'Browse Others',
        markPreviewed: 'Mark as Previewed'
      }
    }
  },
  es: {
    translation: {
      common: {
        loading: 'Cargando...',
        save: 'Guardar',
        cancel: 'Cancelar',
        delete: 'Eliminar',
        edit: 'Editar',
        submit: 'Enviar',
        search: 'Buscar',
        back: 'Atrás',
        next: 'Siguiente',
        previous: 'Anterior',
        yes: 'Sí',
        no: 'No',
        confirm: 'Confirmar',
        close: 'Cerrar',
        error: 'Error',
        success: 'Éxito',
        warning: 'Advertencia',
        info: 'Información'
      },
      nav: {
        home: 'Inicio',
        dashboard: 'Panel',
        profile: 'Perfil',
        settings: 'Configuración',
        logout: 'Cerrar Sesión',
        login: 'Iniciar Sesión',
        register: 'Registrarse'
      },
      auth: {
        email: 'Correo Electrónico',
        password: 'Contraseña',
        confirmPassword: 'Confirmar Contraseña',
        forgotPassword: '¿Olvidaste tu contraseña?',
        loginTitle: 'Iniciar Sesión',
        registerTitle: 'Crear Cuenta',
        loginWithGoogle: 'Continuar con Google',
        loginWithLinkedIn: 'Continuar con LinkedIn',
        orContinueWith: 'O continuar con',
        noAccount: '¿No tienes una cuenta?',
        haveAccount: '¿Ya tienes una cuenta?'
      },
      ged: {
        title: 'Panel de Progreso GED',
        subjects: {
          math: 'Matemáticas',
          rla: 'Lectura y Artes del Lenguaje',
          science: 'Ciencias',
          socialStudies: 'Estudios Sociales'
        },
        readiness: {
          notReady: 'No Listo',
          almostReady: 'Casi Listo',
          ready: 'Listo'
        },
        progress: 'Progreso',
        studyStreak: 'Racha de Estudio',
        daysActive: 'Días Activos',
        subjectsRemaining: 'Materias Restantes',
        nextAction: '¿Qué debo hacer ahora?',
        continueLearning: 'Continuar Aprendiendo',
        takePracticeTest: 'Tomar Examen de Práctica',
        viewCoaches: 'Ver Coaches',
        bookSession: 'Reservar Sesión',
        joinSession: 'Unirse a Sesión',
        library: 'Biblioteca GED',
        reports: 'Reportes de Progreso',
        coaching: 'Coaching'
      },
      chat: {
        title: 'Mensajes',
        newMessage: 'Nuevo Mensaje',
        typeMessage: 'Escribe un mensaje...',
        send: 'Enviar',
        online: 'En línea',
        offline: 'Desconectado',
        typing: 'escribiendo...',
        noMessages: 'No hay mensajes aún',
        startConversation: 'Iniciar una conversación'
      },
      notifications: {
        title: 'Notificaciones',
        preferences: 'Preferencias de Notificación',
        smsEnabled: 'Notificaciones SMS',
        pushEnabled: 'Notificaciones Push',
        emailEnabled: 'Notificaciones por Email',
        sessionReminders: 'Recordatorios de Sesión',
        studyNudges: 'Recordatorios de Estudio',
        progressUpdates: 'Actualizaciones de Progreso',
        saved: 'Preferencias guardadas',
        saveFailed: 'Error al guardar preferencias',
        quietHours: 'Horas Silenciosas',
        testNotification: 'Enviar Notificación de Prueba'
      },
      pricing: {
        title: 'Precios Simples y Transparentes',
        subtitle: 'Elige el plan que se ajuste a tus metas',
        monthly: 'Mensual',
        yearly: 'Anual',
        save: 'Ahorra',
        mostPopular: 'Más Popular',
        startFree: 'Empezar Gratis',
        startTrial: 'Iniciar Prueba',
        subscribe: 'Suscribirse',
        contactSales: 'Contactar Ventas',
        perMonth: '/mes',
        perYear: '/año',
        faq: 'Preguntas Frecuentes',
        securePayment: 'Pago Seguro',
        moneyBack: 'Garantía de 30 Días',
        cancelAnytime: 'Cancela Cuando Quieras'
      },
      fileUpload: {
        upload: 'Subir',
        uploading: 'Subiendo...',
        fileTooLarge: 'Archivo muy grande. Máximo 10MB',
        fileTypeNotAllowed: 'Tipo de archivo no permitido',
        uploadFailed: 'Error al subir archivo',
        sendFile: 'Enviar Archivo',
        attachFile: 'Adjuntar Archivo',
        dragDrop: 'Arrastra y suelta archivos aquí'
      },
      admin: {
        integrations: {
          title: 'Gestión de Integraciones',
          subtitle: 'Administrar credenciales, probar conexiones y monitorear salud',
          status: 'Estado',
          credentials: 'Credenciales',
          health: 'Salud',
          alerts: 'Alertas',
          configured: 'Configurado',
          notConfigured: 'No Configurado',
          healthy: 'Saludable',
          unhealthy: 'No Saludable',
          testConnection: 'Probar Conexión',
          saveCredentials: 'Guardar Credenciales',
          runHealthCheck: 'Ejecutar Revisión',
          allHealthy: '¡Todas las integraciones están saludables!',
          someFailed: 'Algunas integraciones fallaron',
          lastChecked: 'Última revisión',
          manageCredentials: 'Administrar Credenciales',
          selectIntegration: 'Selecciona una integración arriba',
          alertSettings: 'Configuración de Alertas',
          emailAlerts: 'Alertas por Email',
          alertOnFailure: 'Alerta al Fallar',
          alertOnRecovery: 'Alerta al Recuperar',
          checkInterval: 'Intervalo de Revisión'
        }
      },
      simulations: {
        title: 'Simulaciones Interactivas',
        subtitle: 'Aprende con laboratorios virtuales',
        active: 'Activo',
        comingSoon: 'Próximamente',
        browseOthers: 'Ver Otros',
        markPreviewed: 'Marcar como Visto'
      }
    }
  },
  fr: {
    translation: {
      common: {
        loading: 'Chargement...',
        save: 'Enregistrer',
        cancel: 'Annuler',
        delete: 'Supprimer',
        edit: 'Modifier',
        submit: 'Soumettre',
        search: 'Rechercher',
        back: 'Retour',
        next: 'Suivant',
        previous: 'Précédent',
        yes: 'Oui',
        no: 'Non',
        confirm: 'Confirmer',
        close: 'Fermer',
        error: 'Erreur',
        success: 'Succès',
        warning: 'Avertissement',
        info: 'Information'
      },
      nav: {
        home: 'Accueil',
        dashboard: 'Tableau de bord',
        profile: 'Profil',
        settings: 'Paramètres',
        logout: 'Déconnexion',
        login: 'Connexion',
        register: "S'inscrire"
      },
      auth: {
        email: 'Email',
        password: 'Mot de passe',
        confirmPassword: 'Confirmer le mot de passe',
        forgotPassword: 'Mot de passe oublié?',
        loginTitle: 'Connexion',
        registerTitle: 'Créer un compte',
        loginWithGoogle: 'Continuer avec Google',
        loginWithLinkedIn: 'Continuer avec LinkedIn',
        orContinueWith: 'Ou continuer avec',
        noAccount: "Vous n'avez pas de compte?",
        haveAccount: 'Vous avez déjà un compte?'
      },
      ged: {
        title: 'Tableau de Bord GED',
        subjects: {
          math: 'Mathématiques',
          rla: 'Lecture et Arts du Langage',
          science: 'Sciences',
          socialStudies: 'Études Sociales'
        },
        readiness: {
          notReady: 'Pas Prêt',
          almostReady: 'Presque Prêt',
          ready: 'Prêt'
        },
        progress: 'Progrès',
        studyStreak: 'Série d\'Études',
        daysActive: 'Jours Actifs',
        subjectsRemaining: 'Matières Restantes',
        nextAction: 'Que dois-je faire ensuite?',
        continueLearning: 'Continuer à Apprendre',
        takePracticeTest: 'Faire un Test Pratique',
        viewCoaches: 'Voir les Coaches',
        bookSession: 'Réserver une Session',
        joinSession: 'Rejoindre la Session',
        library: 'Bibliothèque GED',
        reports: 'Rapports de Progrès',
        coaching: 'Coaching'
      },
      chat: {
        title: 'Messages',
        newMessage: 'Nouveau Message',
        typeMessage: 'Tapez un message...',
        send: 'Envoyer',
        online: 'En ligne',
        offline: 'Hors ligne',
        typing: 'écrit...',
        noMessages: 'Pas encore de messages',
        startConversation: 'Commencer une conversation'
      },
      notifications: {
        title: 'Notifications',
        preferences: 'Préférences de Notification',
        smsEnabled: 'Notifications SMS',
        pushEnabled: 'Notifications Push',
        emailEnabled: 'Notifications Email',
        sessionReminders: 'Rappels de Session',
        studyNudges: 'Rappels d\'Étude',
        progressUpdates: 'Mises à jour de Progrès'
      }
    }
  },
  zh: {
    translation: {
      common: {
        loading: '加载中...',
        save: '保存',
        cancel: '取消',
        delete: '删除',
        edit: '编辑',
        submit: '提交',
        search: '搜索',
        back: '返回',
        next: '下一步',
        previous: '上一步',
        yes: '是',
        no: '否',
        confirm: '确认',
        close: '关闭',
        error: '错误',
        success: '成功',
        warning: '警告',
        info: '信息'
      },
      nav: {
        home: '首页',
        dashboard: '仪表板',
        profile: '个人资料',
        settings: '设置',
        logout: '登出',
        login: '登录',
        register: '注册'
      },
      auth: {
        email: '电子邮件',
        password: '密码',
        confirmPassword: '确认密码',
        forgotPassword: '忘记密码？',
        loginTitle: '登录',
        registerTitle: '创建账户',
        loginWithGoogle: '使用Google继续',
        loginWithLinkedIn: '使用LinkedIn继续',
        orContinueWith: '或继续使用',
        noAccount: '没有账户？',
        haveAccount: '已有账户？'
      },
      ged: {
        title: 'GED进度仪表板',
        subjects: {
          math: '数学',
          rla: '阅读与语言艺术',
          science: '科学',
          socialStudies: '社会研究'
        },
        readiness: {
          notReady: '未准备好',
          almostReady: '几乎准备好',
          ready: '准备好'
        },
        progress: '进度',
        studyStreak: '学习连续天数',
        daysActive: '活跃天数',
        subjectsRemaining: '剩余科目',
        nextAction: '我下一步应该做什么？',
        continueLearning: '继续学习',
        takePracticeTest: '参加练习测试',
        viewCoaches: '查看教练',
        bookSession: '预约课程',
        joinSession: '加入课程',
        library: 'GED图书馆',
        reports: '进度报告',
        coaching: '辅导'
      },
      chat: {
        title: '消息',
        newMessage: '新消息',
        typeMessage: '输入消息...',
        send: '发送',
        online: '在线',
        offline: '离线',
        typing: '正在输入...',
        noMessages: '暂无消息',
        startConversation: '开始对话'
      },
      notifications: {
        title: '通知',
        preferences: '通知偏好',
        smsEnabled: '短信通知',
        pushEnabled: '推送通知',
        emailEnabled: '邮件通知',
        sessionReminders: '课程提醒',
        studyNudges: '学习提醒',
        progressUpdates: '进度更新'
      }
    }
  },
  ja: {
    translation: {
      common: {
        loading: '読み込み中...',
        save: '保存',
        cancel: 'キャンセル',
        delete: '削除',
        edit: '編集',
        submit: '送信',
        search: '検索',
        back: '戻る',
        next: '次へ',
        previous: '前へ',
        yes: 'はい',
        no: 'いいえ',
        confirm: '確認',
        close: '閉じる',
        error: 'エラー',
        success: '成功',
        warning: '警告',
        info: '情報'
      },
      nav: {
        home: 'ホーム',
        dashboard: 'ダッシュボード',
        profile: 'プロフィール',
        settings: '設定',
        logout: 'ログアウト',
        login: 'ログイン',
        register: '登録'
      },
      ged: {
        title: 'GED進捗ダッシュボード',
        subjects: {
          math: '数学',
          rla: '読解と言語芸術',
          science: '科学',
          socialStudies: '社会科'
        }
      }
    }
  },
  pt: {
    translation: {
      common: {
        loading: 'Carregando...',
        save: 'Salvar',
        cancel: 'Cancelar',
        delete: 'Excluir',
        edit: 'Editar',
        submit: 'Enviar',
        search: 'Pesquisar',
        back: 'Voltar',
        next: 'Próximo',
        previous: 'Anterior'
      },
      nav: {
        home: 'Início',
        dashboard: 'Painel',
        profile: 'Perfil',
        settings: 'Configurações',
        logout: 'Sair',
        login: 'Entrar',
        register: 'Registrar'
      }
    }
  },
  de: {
    translation: {
      common: {
        loading: 'Laden...',
        save: 'Speichern',
        cancel: 'Abbrechen',
        delete: 'Löschen',
        edit: 'Bearbeiten',
        submit: 'Absenden',
        search: 'Suchen',
        back: 'Zurück',
        next: 'Weiter',
        previous: 'Zurück'
      },
      nav: {
        home: 'Startseite',
        dashboard: 'Dashboard',
        profile: 'Profil',
        settings: 'Einstellungen',
        logout: 'Abmelden',
        login: 'Anmelden',
        register: 'Registrieren'
      }
    }
  },
  ko: {
    translation: {
      common: {
        loading: '로딩 중...',
        save: '저장',
        cancel: '취소',
        delete: '삭제',
        edit: '편집',
        submit: '제출',
        search: '검색',
        back: '뒤로',
        next: '다음',
        previous: '이전'
      },
      nav: {
        home: '홈',
        dashboard: '대시보드',
        profile: '프로필',
        settings: '설정',
        logout: '로그아웃',
        login: '로그인',
        register: '가입'
      }
    }
  },
  ar: {
    translation: {
      common: {
        loading: 'جاري التحميل...',
        save: 'حفظ',
        cancel: 'إلغاء',
        delete: 'حذف',
        edit: 'تعديل',
        submit: 'إرسال',
        search: 'بحث',
        back: 'رجوع',
        next: 'التالي',
        previous: 'السابق'
      },
      nav: {
        home: 'الرئيسية',
        dashboard: 'لوحة التحكم',
        profile: 'الملف الشخصي',
        settings: 'الإعدادات',
        logout: 'تسجيل الخروج',
        login: 'تسجيل الدخول',
        register: 'التسجيل'
      }
    }
  },
  hi: {
    translation: {
      common: {
        loading: 'लोड हो रहा है...',
        save: 'सहेजें',
        cancel: 'रद्द करें',
        delete: 'हटाएं',
        edit: 'संपादित करें',
        submit: 'जमा करें',
        search: 'खोजें',
        back: 'वापस',
        next: 'अगला',
        previous: 'पिछला'
      },
      nav: {
        home: 'होम',
        dashboard: 'डैशबोर्ड',
        profile: 'प्रोफाइल',
        settings: 'सेटिंग्स',
        logout: 'लॉग आउट',
        login: 'लॉग इन',
        register: 'पंजीकरण'
      }
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    debug: false,
    interpolation: {
      escapeValue: false
    },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage']
    }
  });

export default i18n;

// Supported languages for UI selector
export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
  { code: 'pt', name: 'Português', flag: '🇧🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'ko', name: '한국어', flag: '🇰🇷' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'hi', name: 'हिंदी', flag: '🇮🇳' }
];
